import React, { useEffect } from "react";
import DOMPurify from 'dompurify';

function contentToHtml(content) {
  if (content == null) return '';
  // Already an HTML string
  if (typeof content === 'string') return content;

  // EditorJS style: blocks with { type, data }
  if (Array.isArray(content)) {
    return content.map((block) => {
      if (!block) return '';
      // EditorJS paragraph
      if (block.type && block.data && typeof block.data.text === 'string') {
        return `<p>${block.data.text}</p>`;
      }
      // Sanity block: {_type: 'block', children: [{text}] }
      if (block._type === 'block' && Array.isArray(block.children)) {
        const txt = block.children.map(c => c.text || '').join('');
        return `<p>${txt}</p>`;
      }
      // Generic object with children
      if (block.children && Array.isArray(block.children)) {
        const txt = block.children.map(c => c.text || '').join('');
        return `<p>${txt}</p>`;
      }
      // Image block
      if (block.type === 'image' && block.data && (block.data.file || block.data.url || block.data.src)) {
        const src = block.data.url || block.data.src || (block.data.file && block.data.file.url) || '';
        return src ? `<img src="${src}" alt="" />` : '';
      }
      // Fallback: try to stringify small objects
      try {
        return `<pre style="white-space:pre-wrap">${JSON.stringify(block)}</pre>`;
      } catch (e) {
        return '';
      }
    }).join('');
  }

  // Single object (EditorJS single block or Sanity single block)
  if (typeof content === 'object') {
    // EditorJS block
    if (content.type && content.data && typeof content.data.text === 'string') {
      return `<p>${content.data.text}</p>`;
    }
    if (content._type === 'block' && Array.isArray(content.children)) {
      const txt = content.children.map(c => c.text || '').join('');
      return `<p>${txt}</p>`;
    }
    if (content.children && Array.isArray(content.children)) {
      const txt = content.children.map(c => c.text || '').join('');
      return `<p>${txt}</p>`;
    }
    return `<pre style="white-space:pre-wrap">${JSON.stringify(content)}</pre>`;
  }

  return '';
}

function SafeRichText({ content }) {
  const html = contentToHtml(content);

  // If we received a string of HTML from WYSIWYG editors (TinyMCE),
  // remove inline styles and unwrap span/font/div tags to keep markup clean.
  const sanitizeStyleString = (styleString) => {
    if (!styleString) return '';
    const allowedProps = ['color', 'font-weight', 'font-style', 'text-decoration', 'text-decoration-color', 'text-decoration-line', 'text-decoration-style', 'text-align'];
    return styleString.split(';').map(s => s.trim()).filter(Boolean).map(decl => {
      const [prop, ...rest] = decl.split(':');
      if (!prop || rest.length === 0) return null;
      const name = prop.trim().toLowerCase();
      const value = rest.join(':').trim();
      if (allowedProps.includes(name)) return `${name}: ${value}`;
      return null;
    }).filter(Boolean).join('; ');
  };

  const cleanHtmlString = (rawHtml) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawHtml, 'text/html');

      const allowedTags = new Set(['P','BR','STRONG','B','EM','I','U','UL','OL','LI','A','H1','H2','H3','H4','H5','H6','IMG','SPAN']);
      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT, null, false);
      const toProcess = [];
      while(walker.nextNode()) toProcess.push(walker.currentNode);

      toProcess.forEach(node => {
        // Sanitize style and class attributes
        if (node.hasAttribute) {
          try {
            const rawStyle = node.getAttribute('style');
            if (rawStyle) {
              const safeStyle = sanitizeStyleString(rawStyle);
              if (safeStyle) node.setAttribute('style', safeStyle);
              else node.removeAttribute('style');
            }
          } catch (e) {}
          try { node.removeAttribute('id'); } catch(e) {}
        }

        const tag = node.tagName.toUpperCase();

        if (!allowedTags.has(tag)) {
          // Unwrap the node: replace it with its children
          const parent = node.parentNode;
          if (!parent) return;
          while (node.firstChild) parent.insertBefore(node.firstChild, node);
          parent.removeChild(node);
        } else {
          // Allowed tags: strip all attributes except a[href], img[src|alt]
          if (tag === 'A') {
            const href = node.getAttribute('href');
            // Reset attributes and re-add sanitized href
            const safeHref = href ? href : null;
            Array.from(node.attributes).forEach(attr => node.removeAttribute(attr.name));
            if (safeHref) node.setAttribute('href', safeHref);
            node.setAttribute('rel','noopener noreferrer');
            node.setAttribute('target','_blank');
          } else if (tag === 'IMG') {
            const src = node.getAttribute('src');
            const alt = node.getAttribute('alt');
            Array.from(node.attributes).forEach(attr => node.removeAttribute(attr.name));
            if (src) node.setAttribute('src', src);
            if (alt) node.setAttribute('alt', alt);
          } else {
            // Preserve icon/font classes for known icon libraries (FontAwesome, Material Icons, Bootstrap Icons)
            const allowClassRegex = /(^fa-|^fa$|^material-icons|^bi-|^icon-)/i;
            Array.from(node.attributes).forEach(attr => {
              try {
                if (attr.name === 'class' && allowClassRegex.test(attr.value)) {
                  // keep class attribute for icon fonts
                  return;
                }
                if (attr.name === 'style') return; // already sanitized
                // allow src/alt on img already handled; otherwise remove
                node.removeAttribute(attr.name);
              } catch (e) {}
            });
          }
        }
      });

      return doc.body.innerHTML || '';
    } catch (e) {
      // Fallback: strip tags and return plain text
      return rawHtml.replace(/<[^>]+>/g, '');
    }
  };

  // Post-process sanitized HTML to remove empty paragraphs and collapse multiple <br>
  const postProcessHtml = (s) => {
    if (!s || typeof s !== 'string') return s;
    // Normalize whitespace between tags first
    s = s.replace(/>\s+</g, '><');

    // Remove leading/trailing <br> inside the content
    s = s.replace(/^(?:\s|<br\s*\/?\s*>)+/i, '');
    s = s.replace(/(?:\s|<br\s*\/?\s*>)+$/i, '');

    // Collapse multiple consecutive <br> into a single <br/>
    s = s.replace(/(<br\s*\/?\s*>\s*){2,}/gi, '<br/>');

    // Remove empty paragraphs (only whitespace, non-breaking spaces or lone <br>)
    s = s.replace(/<p>(?:\s|&nbsp;|<br\s*\/?\s*>)*<\/p>/gi, '');

    // Remove paragraphs that contain only non-breaking spaces or zero-width spaces
    s = s.replace(/<p>(?:&nbsp;|\u200B|\s)*<\/p>/gi, '');

    // Collapse adjacent empty-inline tags and remove accidental multiple wrappers
    // e.g., <p><span></span></p> -> removed by empty p rule above

    // Remove consecutive block-level empty nodes left over
    s = s.replace(/(?:<(?:div|p|section)[^>]*>\s*<\/(?:div|p|section)>\s*)+/gi, '');

    // Trim again
    return s.trim();
  };

  const cleaned = typeof html === 'string' ? cleanHtmlString(html) : html;
  const sanitized = DOMPurify.sanitize(cleaned);
  const clean = typeof sanitized === 'string' ? postProcessHtml(sanitized) : sanitized;

  useEffect(() => {
    const styleId = 'safe-rich-styles';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .safe-rich a { color: #1e40af; text-decoration: underline; }
      .safe-rich a:visited { color: #4f46e5; }
      .safe-rich a:hover { opacity: 0.9; }
      .safe-rich p { margin: 0 0 0.6rem 0 !important; font-size: 1rem; line-height: 1.6; color: inherit; }
      .safe-rich ul, .safe-rich ol { margin: 0 0 0.9rem 1.1rem; padding-left: 1.1rem; }
      .safe-rich li { margin-bottom: 0.45rem; }
      .safe-rich img { max-width: 100%; height: auto; display: block; margin: 0.6rem 0; border-radius: 8px; }
      .safe-rich h1, .safe-rich h2, .safe-rich h3, .safe-rich h4 { margin: 0 0 0.6rem 0; line-height: 1.2; }
      .safe-rich blockquote { margin: 0 0 1rem 0; padding-left: 1rem; border-left: 3px solid rgba(0,0,0,0.06); color: #374151; }

      /* Mobile-specific tweaks */
      @media (max-width: 768px) {
        .safe-rich { font-size: 0.98rem; }
        .safe-rich p { font-size: 0.98rem; margin-bottom: 0.5rem !important; }
        .safe-rich h1 { font-size: 1.25rem; margin-bottom: 0.5rem !important; }
        .safe-rich h2 { font-size: 1.05rem; margin-bottom: 0.45rem !important; }
        .safe-rich h3 { font-size: 1rem; margin-bottom: 0.4rem !important; }
      }
      @media (max-width: 420px) {
        .safe-rich { font-size: 0.95rem; }
        .safe-rich p { margin-bottom: 0.45rem !important; }
        .safe-rich ul, .safe-rich ol { margin-left: 0.9rem; padding-left: 0.9rem; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      // keep the style for app lifetime; do not remove on unmount
    };
  }, []);

  return <div className="safe-rich" dangerouslySetInnerHTML={{ __html: clean }} />;
}

export default SafeRichText;
